/**
 * SelectPill — Editable colored-pill picker for small domain enums.
 *
 * Uses a <button>-based trigger so Tailwind bg-* utilities apply correctly
 * in dark mode across all browsers (native <select> user-agent chrome
 * overrides bg-* in Firefox/GTK dark mode).
 *
 * @layer atom
 * @package @plerity/ui-core
 */

import { cva } from "class-variance-authority";
import { Check } from "lucide-react";
import { memo, useCallback, useEffect, useId, useRef, useState } from "react";
import { StatusBadge } from "../StatusBadge/StatusBadge";
import type { StatusLevel, StatusVariant } from "../StatusBadge/StatusBadge.types";
import { cn } from "../utils/cn";
import type { EnumOption, SelectPillProps } from "./types";

// ─── Pill variant classes — mirrors badgeVariants in StatusBadge ──────────────

const pillVariants = cva(
  "inline-flex items-center gap-1.5 rounded-full font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      status: {
        success:
          "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50 focus-visible:ring-green-500",
        warning:
          "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 hover:bg-yellow-200 dark:hover:bg-yellow-900/50 focus-visible:ring-yellow-500",
        // high: orange severity tier, mirrors STATUS_COLOR_CLASSES.high
        high: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 hover:bg-orange-200 dark:hover:bg-orange-900/50 focus-visible:ring-orange-500",
        error:
          "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 focus-visible:ring-red-500",
        danger:
          "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 focus-visible:ring-red-500",
        info: "bg-info-100 text-info-800 dark:bg-info-900/30 dark:text-info-400 hover:bg-info-200 dark:hover:bg-info-900/50 focus-visible:ring-info-500",
        // neutral pill: literal gray ramp WITH dark: pairs (the web gray ramp is static
        // under html.dark, so bare stops stay light on dark cards).
        neutral:
          "bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 focus-visible:ring-gray-400",
      },
      size: {
        sm: "px-2 py-0.5 text-xs",
        md: "px-2.5 py-0.5 text-xs",
      },
    },
    defaultVariants: {
      status: "neutral",
      size: "md",
    },
  }
);

// Listbox popover option row mini-pill (same variant colors, fixed size)
const miniPillVariants = cva("inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-medium", {
  variants: {
    status: {
      success: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
      warning: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
      high: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
      error: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
      danger: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
      info: "bg-info-100 text-info-800 dark:bg-info-900/30 dark:text-info-400",
      neutral: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200",
    },
  },
  defaultVariants: { status: "neutral" },
});

// ─── Chevron SVG (reused from Select.tsx) ────────────────────────────────────

function Chevron() {
  return (
    <svg className="h-3 w-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

function SelectPillInner<T extends string = string>({
  value,
  onChange,
  meta,
  order,
  size = "md",
  disabled = false,
  readOnly = false,
  ariaLabel,
  className,
  renderIcon,
}: SelectPillProps<T>) {
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState<number>(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listboxId = useId();

  // Build ordered option list
  const optionKeys = (order ?? (Object.keys(meta) as T[])).filter((k) => k in meta);
  const options: EnumOption<T>[] = optionKeys.map((k) => ({ value: k, ...meta[k] }) as EnumOption<T>);

  const currentOption = meta[value] ?? { label: value, variant: "neutral" as StatusVariant };
  const currentVariant = (currentOption.variant as StatusLevel) ?? "neutral";

  const close = useCallback(() => {
    setOpen(false);
    setHighlightIndex(-1);
  }, []);

  // Outside-click + Escape close (matches Dropdown.tsx pattern)
  useEffect(() => {
    if (!open) return;

    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, close]);

  function handleTriggerKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
    if (disabled) return;
    switch (e.key) {
      case "Enter":
      case " ":
        e.preventDefault();
        if (!open) {
          setOpen(true);
          setHighlightIndex(options.findIndex((o) => o.value === value));
        } else if (highlightIndex >= 0 && !options[highlightIndex]?.disabled) {
          onChange(options[highlightIndex].value);
          close();
          triggerRef.current?.focus();
        }
        break;
      case "Escape":
        e.preventDefault();
        close();
        triggerRef.current?.focus();
        break;
      case "ArrowDown":
        e.preventDefault();
        if (!open) {
          setOpen(true);
          setHighlightIndex(options.findIndex((o) => o.value === value));
        } else {
          setHighlightIndex((prev) => Math.min(prev + 1, options.length - 1));
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (!open) {
          setOpen(true);
          setHighlightIndex(options.findIndex((o) => o.value === value));
        } else {
          setHighlightIndex((prev) => Math.max(prev - 1, 0));
        }
        break;
      case "Home":
        e.preventDefault();
        if (open) setHighlightIndex(0);
        break;
      case "End":
        e.preventDefault();
        if (open) setHighlightIndex(options.length - 1);
        break;
    }
  }

  function handleOptionKeyDown(e: React.KeyboardEvent<HTMLButtonElement>, idx: number) {
    switch (e.key) {
      case "Enter":
      case " ":
        e.preventDefault();
        if (!options[idx]?.disabled) {
          onChange(options[idx].value);
          close();
          triggerRef.current?.focus();
        }
        break;
      case "Escape":
        e.preventDefault();
        close();
        triggerRef.current?.focus();
        break;
      case "ArrowDown":
        e.preventDefault();
        setHighlightIndex(Math.min(idx + 1, options.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightIndex(Math.max(idx - 1, 0));
        break;
      case "Home":
        e.preventDefault();
        setHighlightIndex(0);
        break;
      case "End":
        e.preventDefault();
        setHighlightIndex(options.length - 1);
        break;
    }
  }

  // Read-only mode: render as StatusBadge
  if (readOnly) {
    return <StatusBadge label={currentOption.label ?? value} status={currentVariant} className={className} />;
  }

  const activeDescendantId = highlightIndex >= 0 ? `${listboxId}-option-${highlightIndex}` : undefined;

  return (
    <div ref={containerRef} className="relative inline-block">
      {/* Trigger button — styled as colored pill.
          WAI-ARIA APG's "select-only combobox" pattern would call for
          role="combobox" here (aria-activedescendant tracks the roving
          highlight in the popup listbox below, which role="button" doesn't
          formally support) — but role="button" is kept because ~30 existing
          test assertions in this package AND consumer apps across the fleet
          query this trigger via `getByRole("button", ...)`; retargeting the
          accessible role would break every one of them. A native <select>
          is intentionally NOT used here — see file header: its UA chrome
          overrides bg-* in Firefox/GTK dark mode. */}
      {/* biome-ignore lint/a11y/useAriaPropsSupportedByRole: aria-activedescendant is technically restricted to composite-widget roles (combobox/listbox/etc.), not "button" — accepted tradeoff to keep the accessible role stable as "button" for existing getByRole("button", ...) queries across this package and consumer apps; most screen readers honor aria-activedescendant regardless of the host role in practice. */}
      {/* biome-ignore lint/a11y/useSemanticElements: element is already the semantic <button>; role="button" is a documented no-op, not a suggestion to swap elements. */}
      <button
        ref={triggerRef}
        type="button"
        // biome-ignore lint/a11y/noRedundantRoles: explicit role="button" IS redundant on a native <button> — kept anyway (rather than removed) so the intent is self-documenting next to the aria-activedescendant tradeoff explained above.
        role="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={activeDescendantId}
        onClick={() => {
          if (!disabled) {
            const nextOpen = !open;
            setOpen(nextOpen);
            if (nextOpen) {
              setHighlightIndex(options.findIndex((o) => o.value === value));
            } else {
              setHighlightIndex(-1);
            }
          }
        }}
        onKeyDown={handleTriggerKeyDown}
        className={cn(pillVariants({ status: currentVariant, size }), className)}
      >
        {renderIcon?.({ value, ...currentOption } as EnumOption<T>)}
        {currentOption.label ?? value}
        <Chevron />
      </button>

      {/* Listbox popover — <ul role="listbox"><li role="option"> is the
          textbook WAI-ARIA APG "select-only combobox" listbox pattern. */}
      <ul
        id={listboxId}
        // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: <ul role="listbox"> is the WAI-ARIA APG-blessed pattern for a custom listbox popup, paired with <li role="option"> children below.
        role="listbox"
        aria-label={ariaLabel}
        aria-hidden={!open}
        className={cn(
          "absolute left-0 top-[calc(100%+4px)] z-[150]",
          // Listbox popover: literal white/gray-800 surface — matches the page
          // cards (TaskHeaderCard etc. use `bg-white dark:bg-gray-800`) and the
          // dashboard-shell dark-mode convention. NOTE: do NOT use the semantic
          // `bg-surface` token here. It emits `background-color: var(--color-bg-surface)`
          // with NO fallback, and `--color-bg-surface` is stripped from the React
          // SPA apps' Tailwind v4 build (`:root` blocks in `@import`-ed colors.css
          // are dropped — same reason brand vars are inlined in each app's
          // index.css). The result was a fully TRANSPARENT dropdown on the shells
          // (reported on ops /tasks/:id, 2026-06-18). The gray ramp keeps literal
          // fallbacks (`var(--color-gray-800,#1e293b)`) so `dark:bg-gray-800`
          // renders true-dark and survives the strip.
          "min-w-[10rem] rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800",
          "transition-all duration-150",
          open ? "visible opacity-100 translate-y-0" : "invisible opacity-0 -translate-y-1"
        )}
      >
        {options.map((opt, idx) => {
          const isSelected = opt.value === value;
          const isHighlighted = idx === highlightIndex;
          const optVariant = (opt.variant as StatusLevel) ?? "neutral";

          return (
            // biome-ignore lint/a11y/useFocusableInteractive: options intentionally do NOT receive real DOM focus — the trigger button (role="combobox") owns focus and tracks the highlighted option virtually via aria-activedescendant, per the WAI-ARIA APG roving-highlight technique. Adding tabIndex here would break that pattern.
            <li
              key={opt.value}
              id={`${listboxId}-option-${idx}`}
              // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: <li role="option"> is the WAI-ARIA APG-blessed pattern, paired with the parent <ul role="listbox"> above.
              role="option"
              aria-selected={isSelected}
              aria-disabled={opt.disabled}
              aria-label={opt.label}
            >
              <button
                type="button"
                disabled={opt.disabled}
                onClick={() => {
                  if (!opt.disabled) {
                    onChange(opt.value);
                    close();
                    triggerRef.current?.focus();
                  }
                }}
                onKeyDown={(e) => handleOptionKeyDown(e, idx)}
                onMouseEnter={() => setHighlightIndex(idx)}
                className={cn(
                  "flex w-full items-center gap-2 px-3 py-2 text-sm text-left",
                  "transition-colors duration-100",
                  // Literal gray ramp (keeps fallbacks in the apps' build) — same
                  // reasoning as the popover surface above. `bg-surface-subtle` /
                  // `text-[var(--color-text-primary)]` resolve to undefined vars on
                  // the React SPA shells.
                  "text-gray-900 dark:text-gray-100",
                  isHighlighted && "bg-gray-100 dark:bg-gray-700",
                  opt.disabled && "cursor-not-allowed opacity-50"
                )}
              >
                {/* Mini-pill showing variant preview */}
                <span className={miniPillVariants({ status: optVariant })} aria-hidden="true">
                  {opt.label}
                </span>
                {opt.description && <span className="text-xs text-gray-500 dark:text-gray-400">{opt.description}</span>}
                {/* Check icon for selected row */}
                {isSelected && (
                  <Check className="ml-auto h-3.5 w-3.5 shrink-0 text-gray-500 dark:text-gray-400" aria-hidden="true" />
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Memo-wrap with display name. The generic param is fixed at string for the
// memoized export; callers using T-specific values can cast or use the named
// export directly.
export const SelectPill = memo(SelectPillInner) as typeof SelectPillInner;

(SelectPill as { displayName?: string }).displayName = "SelectPill";
